import React, { useState, useEffect } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

// Maximum file sizes (in bytes)
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB for videos (Supabase default limit)

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const ProjectForm = ({ onSubmit, initialData, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    full_description: '',
    details: '',
    impact: '',
    image_file: null,
    additional_images: [],
    videos: []
  });
  const [preview, setPreview] = useState(null);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [fileError, setFileError] = useState(null);

  // Existing media already stored on the project (filenames + preview URLs).
  // These can be removed when editing; the ones that remain are kept on save.
  const [existingImages, setExistingImages] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [existingMainImage, setExistingMainImage] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        details: Array.isArray(initialData.details)
          ? initialData.details.join('\n')
          : initialData.details || '',
        image_file: null,
        additional_images: [],
        videos: []
      });

      // Load existing media so it can be previewed and removed while editing
      const imageFiles = (initialData.additional_images || []).filter(Boolean);
      const videoFiles = (initialData.videos || []).filter(Boolean);

      let cancelled = false;
      const buildSignedUrl = async (name) => {
        const { data } = await supabase.storage
          .from('Project-images')
          .createSignedUrl(`Projecten/${name}`, 3600);
        return data?.signedUrl || null;
      };

      const loadExisting = async () => {
        const [images, videos, mainUrl] = await Promise.all([
          Promise.all(imageFiles.map(async (name) => ({ name, url: await buildSignedUrl(name) }))),
          Promise.all(videoFiles.map(async (name) => ({ name, url: await buildSignedUrl(name) }))),
          initialData.image_url ? buildSignedUrl(initialData.image_url) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setExistingImages(images);
        setExistingVideos(videos);
        setExistingMainImage(initialData.image_url ? { name: initialData.image_url, url: mainUrl } : null);
      };

      loadExisting();
      return () => { cancelled = true; };
    }

    // Reset existing media when switching to "new project" mode
    setExistingImages([]);
    setExistingVideos([]);
    setExistingMainImage(null);
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_IMAGE_SIZE) {
        setFileError(`Afbeelding "${file.name}" is te groot (${formatFileSize(file.size)}). Maximum is ${formatFileSize(MAX_IMAGE_SIZE)}.`);
        e.target.value = '';
        return;
      }
      setFileError(null);
      setFormData(prev => ({ ...prev, image_file: file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const oversizedFiles = files.filter(file => file.size > MAX_IMAGE_SIZE);
    
    if (oversizedFiles.length > 0) {
      setFileError(`${oversizedFiles.length} afbeelding(en) te groot. Maximum is ${formatFileSize(MAX_IMAGE_SIZE)}: ${oversizedFiles.map(f => f.name).join(', ')}`);
      e.target.value = '';
      return;
    }
    
    setFileError(null);
    setFormData(prev => ({
      ...prev,
      additional_images: [...prev.additional_images, ...files]
    }));
    
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setAdditionalPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    const oversizedFiles = files.filter(file => file.size > MAX_VIDEO_SIZE);
    
    if (oversizedFiles.length > 0) {
      setFileError(`Video('s) te groot. Maximum is ${formatFileSize(MAX_VIDEO_SIZE)}: ${oversizedFiles.map(f => `${f.name} (${formatFileSize(f.size)})`).join(', ')}`);
      e.target.value = '';
      return;
    }
    
    setFileError(null);
    setFormData(prev => ({
      ...prev,
      videos: [...prev.videos, ...files]
    }));
    
    const newPreviews = files.map(file => ({
      url: URL.createObjectURL(file),
      name: file.name,
      size: formatFileSize(file.size)
    }));
    setVideoPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
    setVideoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeAdditionalImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images.filter((_, i) => i !== index)
    }));
    setAdditionalPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingVideo = (index) => {
    setExistingVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Process details into array before submitting
    const processedFormData = {
      ...formData,
      details: formData.details.split('\n').filter(detail => detail.trim() !== ''),
      // Existing media the user chose to keep (filenames only)
      existing_additional_images: existingImages.map(img => img.name),
      existing_videos: existingVideos.map(video => video.name)
    };

    onSubmit(processedFormData);

    setFormData({
      title: '',
      description: '',
      full_description: '',
      details: '',
      impact: '',
      image_file: null,
      additional_images: [],
      videos: []
    });
    setPreview(null);
    setAdditionalPreviews([]);
    setVideoPreviews([]);
    setExistingImages([]);
    setExistingVideos([]);
    setExistingMainImage(null);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-8">
      {fileError && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <AlertCircle size={20} />
          <span>{fileError}</span>
          <button 
            type="button" 
            onClick={() => setFileError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X size={18} />
          </button>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Titel
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Korte Beschrijving
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Volledige Beschrijving
          </label>
          <textarea
            name="full_description"
            value={formData.full_description}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Details (één punt per regel)
          </label>
          <textarea
            name="details"
            value={formData.details}
            onChange={handleChange}
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="Bijvoorbeeld:&#10;Verstrekking van pakketten&#10;Ondersteuning van families&#10;Impact op de gemeenschap"
          />
          <div className="mt-3 p-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-500 mb-2">Voorvertoning:</p>
            <ul className="list-disc pl-5 space-y-1">
              {formData.details.split('\n')
                .filter(detail => detail.trim() !== '')
                .map((detail, index) => (
                  <li key={index} className="text-sm text-gray-600">{detail}</li>
                ))
              }
            </ul>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Impact
          </label>
          <textarea
            name="impact"
            value={formData.impact}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="Beschrijf de impact van dit project..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hoofdafbeelding <span className="text-gray-400 font-normal">(max 10MB)</span>
          </label>
          <div className="flex items-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              id="main-image"
              required={!initialData}
            />
            <label
              htmlFor="main-image"
              className="cursor-pointer flex items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500"
            >
              {preview ? (
                <img src={preview} alt="Preview" className="w-full h-full object-cover rounded-lg" />
              ) : existingMainImage?.url ? (
                <img src={existingMainImage.url} alt="Huidige hoofdafbeelding" className="w-full h-full object-cover rounded-lg" />
              ) : (
                <Upload className="w-8 h-8 text-gray-400" />
              )}
            </label>
            {(preview || existingMainImage) && (
              <p className="ml-4 text-sm text-gray-500">
                Klik op de afbeelding om een andere te kiezen
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Extra Afbeeldingen <span className="text-gray-400 font-normal">(max 10MB per afbeelding)</span>
          </label>
          <div className="flex flex-wrap gap-4">
            <input
              type="file"
              accept="image/*"
              onChange={handleAdditionalImagesChange}
              className="hidden"
              id="additional-images"
              multiple
            />
            <label
              htmlFor="additional-images"
              className="cursor-pointer flex items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500"
            >
              <Upload className="w-8 h-8 text-gray-400" />
            </label>
            {existingImages.map((image, index) => (
              <div key={`existing-img-${index}`} className="relative w-32 h-32">
                <img
                  src={image.url}
                  alt={`Bestaande afbeelding ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(index)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            {additionalPreviews.map((preview, index) => (
              <div key={index} className="relative w-32 h-32">
                <img 
                  src={preview} 
                  alt={`Preview ${index + 1}`} 
                  className="w-full h-full object-cover rounded-lg"
                />
                <a> <button
                  type="button"
                  onClick={() => removeAdditionalImage(index)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
                </a> 
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Video's <span className="text-gray-400 font-normal">(max 50MB per video)</span>
          </label>
          <div className="flex flex-wrap gap-4">
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="hidden"
              id="video-upload"
              multiple
            />
            <label
              htmlFor="video-upload"
              className="cursor-pointer flex items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500"
            >
              <div className="text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                <span className="text-xs text-gray-500 mt-1">Video's</span>
              </div>
            </label>
            {existingVideos.map((video, index) => (
              <div key={`existing-video-${index}`} className="relative w-32 h-32">
                <video
                  src={video.url}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeExistingVideo(index)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            {videoPreviews.map((video, index) => (
              <div key={index} className="relative w-32 h-32">
                <video
                  src={video.url}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b-lg">
                  <div className="truncate">{video.name}</div>
                  <div className="text-green-300">{video.size}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeVideo(index)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4 pt-6 border-t">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Annuleren
          </button>
        )}
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {initialData ? 'Project Bijwerken' : 'Project Toevoegen'}
        </button>
      </div>
    </form>
  );
};

export default ProjectForm;